import {
  DragDrop,
  FormControlLabel,
  GKButton,
  GKSearchSelect,
  GKSideModal,
  GKTextField,
  Grid2,
  Show,
  Switch,
  Typography,
} from "components";
import { useMutation } from "hooks/useQueries";
import { useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import axiosInstance from "utils/axiosInstance";
import { GKModalProps } from "utils/commonInterface";
import { refetchQuery } from "utils/helpers";

export interface SheetDetails {
  id: number;
  orderBy: string;
  name: string;
  createdOn: string;
  formatJson: FormatJson[];
  isOdinSheet: boolean;
  company: number;
  createdBy: number;
  gridkeyFields: string[];
}

export interface FormatJson {
  customField: string;
  defaultBool: boolean;
  defaultValue: any;
  gridkeyField: string;
}

const ExecutionModal = (
  props: GKModalProps & { sheetDetails?: SheetDetails },
) => {
  const { open, setOpen, sheetDetails } = props;

  const [uploadError, setUploadError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: {
      activeStep: sheetDetails?.id ? 2 : 1,
      step1: {
        file: [],
      },
      step2: {
        name: sheetDetails?.id ? sheetDetails.name : "",
        sheetFields: sheetDetails?.id
          ? sheetDetails?.formatJson?.map((dataItem: any) => {
              return {
                customField: dataItem?.customField,
                gridkeyField: dataItem?.gridkeyField,
                defaultValue: dataItem?.defaultValue,
                defaultBool: dataItem?.defaultBool,
              };
            })
          : [
              {
                customField: "",
                gridkeyField: "",
                defaultValue: "",
                defaultBool: false,
              },
            ],
      },
      fileResponse: {
        customFields: [],
        gridkeyFields: [],
      },
    },
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: "step2.sheetFields",
  });

  const orderMapping = useMutation({
    mutationFn: (data: any) =>
      axiosInstance.post("/order-manager-mappings/", data),
    onSuccess: (response: {
      data: {
        data: {
          gridkeyFields: string[];
          customFields: string[];
        };
      };
    }) => {
      form.setValue("fileResponse", response?.data?.data);
      form.setValue(
        "step2.sheetFields",
        response?.data?.data?.customFields?.map((dataItem) => {
          return {
            customField: dataItem || "",
            gridkeyField: "",
            defaultValue: "",
            defaultBool: false,
          };
        }),
      );
      form.setValue("activeStep", form.getValues("activeStep") + 1);
    },
  });

  const createOrderMapping = useMutation({
    mutationFn: (data) =>
      sheetDetails?.id
        ? axiosInstance?.patch(`order-manager-mappings/mapping/`, data)
        : axiosInstance?.post(`order-manager-mappings/mapping/`, data),
    onSuccess: () => {
      setOpen(false);
      refetchQuery("ORDER_MANAGER_MAPPING");
    },
  });

  return (
    <GKSideModal
      width="80%"
      setOpen={setOpen}
      open={open}
      modalTitle="Execution Sheet"
      footerButton={
        <Grid2
          container
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap="10px"
        >
          <GKButton
            variant="contained"
            type="submit"
            form="order-mapping-form"
            loading={orderMapping.isLoading || createOrderMapping.isLoading}
          >
            Save
          </GKButton>
        </Grid2>
      }
    >
      <form
        id="order-mapping-form"
        onSubmit={form.handleSubmit((values) => {
          if (values.activeStep === 1) {
            const data = new FormData();
            values.step1.file.forEach((file) => {
              data.append("file", file);
            });
            orderMapping.mutate(data);
          } else if (values.activeStep === 2) {
            const responseData = values?.step2.sheetFields?.map(
              (dataItem: any) => {
                return {
                  ...dataItem,
                  defaultValue: dataItem?.defaultBool
                    ? dataItem.defaultValue
                    : null,
                  gridkeyField: !dataItem?.defaultBool
                    ? dataItem.gridkeyField
                    : null,
                };
              },
            );

            createOrderMapping.mutate({
              name: values.step2.name,
              formatJson: responseData,
              id: sheetDetails?.id && sheetDetails?.id,
            });
          }
        })}
      >
        <Grid2 container spacing={2}>
          <Show.When isTrue={form.watch("activeStep") === 1}>
            <Grid2 size={12}>
              <Controller
                control={form.control}
                name="step1.file"
                render={({ field }) => (
                  <DragDrop
                    {...field}
                    onChange={(e) => field.onChange(e)}
                    acceptType={".csv"}
                    uploadError={uploadError}
                    setUploadError={setUploadError}
                  />
                )}
              />
            </Grid2>
          </Show.When>
          <Show.When isTrue={form.watch("activeStep") === 2}>
            <Grid2 size={12}>
              <Controller
                control={form.control}
                name="step2.name"
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <GKTextField {...field} inputLabel="Name" error={!!error} />
                )}
              />
            </Grid2>
            {fields.map((row, index) => {
              return (
                <Grid2
                  container
                  size={12}
                  spacing={1}
                  key={row.id}
                  display={"flex"}
                  alignItems={"center"}
                >
                  <Grid2 size={3}>
                    <Controller
                      control={form.control}
                      name={`step2.sheetFields.${index}.customField`}
                      render={({ field }) => (
                        <Typography>{field.value || "-"}</Typography>
                      )}
                    />
                  </Grid2>
                  <Grid2 size={3}>
                    <Controller
                      control={form.control}
                      name={`step2.sheetFields.${index}.defaultBool`}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value}
                              onChange={(event) =>
                                field.onChange(event.target.checked)
                              }
                            />
                          }
                          label="Custom Field"
                        />
                      )}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <Show.When
                      isTrue={form.watch(
                        `step2.sheetFields.${index}.defaultBool`,
                      )}
                    >
                      <Controller
                        control={form.control}
                        name={`step2.sheetFields.${index}.defaultValue`}
                        rules={{ required: true }}
                        render={({ field, fieldState: { error } }) => (
                          <GKTextField
                            {...field}
                            inputLabel=""
                            placeholder="Enter field name"
                            error={!!error}
                          />
                        )}
                      />
                    </Show.When>
                    <Show.When
                      isTrue={
                        !form.watch(`step2.sheetFields.${index}.defaultBool`)
                      }
                    >
                      <Controller
                        control={form.control}
                        name={`step2.sheetFields.${index}.gridkeyField`}
                        rules={{ required: true }}
                        render={({ field, fieldState: { error } }) => (
                          <GKSearchSelect
                            {...field}
                            inputLabel=""
                            disableClearable={false}
                            options={
                              (sheetDetails?.id
                                ? sheetDetails
                                : form.getValues("fileResponse")
                              )?.gridkeyFields.map((item) => {
                                return { name: item, value: item };
                              }) || []
                            }
                            value={
                              (sheetDetails?.id
                                ? sheetDetails
                                : form.getValues("fileResponse")
                              )?.gridkeyFields
                                .map((item) => {
                                  return { name: item, value: item };
                                })
                                .find((item) => item.value === field.value)
                                ?.name
                            }
                            onChange={(event, val) =>
                              field.onChange(val ? val?.value : "")
                            }
                            getOptionLabel={(option) => option.label || option}
                            error={!!error}
                          />
                        )}
                      />
                    </Show.When>
                  </Grid2>
                </Grid2>
              );
            })}
          </Show.When>
        </Grid2>
      </form>
    </GKSideModal>
  );
};

export default ExecutionModal;
